import type { ResponseError } from '@app/utils/types'

import SFetch from '@app/utils/sfetch'
import { abort, getSignal } from '@app/adapters/abort'
import { isResponseError } from '@app/utils/predicates'

import api from '../api'
import { CHUNK_FETCH_BATCH_SIZE, MAX_CHUNK_RETRIES } from '../store/constants'

const ABORT_KEY = 'insightsCompaniesOverview'

export type ChunkFetchArgs = {
  requestRef: string
  chunkIndex: number
  signal?: AbortSignal
}

export type ChunkFetchResult = {
  matches?: Array<Record<string, unknown>>
  non_matches?: Array<number>
} | null

const shouldRetryChunk = (
  response: ChunkFetchResult | ResponseError
): boolean => {
  if (!isResponseError(response)) return false
  const { status } = response
  // status 0 covers both abort and genuine network failures — bail out in both
  // cases rather than wasting retry budget on requests that can't succeed.
  if (status === 0) return false
  return status === 404 || status >= 500
}

// api/ is still untyped JavaScript, so we wrap it with the precise signature
// we rely on here. Remove the cast once api/ is migrated to TypeScript.
const fetchChunkResults = (args: ChunkFetchArgs): Promise<ChunkFetchResult> =>
  (
    api.fetchChunkResults as (args: ChunkFetchArgs) => Promise<ChunkFetchResult>
  )(args)

export const fetchChunkWithRetry = SFetch.withRetry<
  [ChunkFetchArgs],
  ChunkFetchResult
>(fetchChunkResults, {
  maxRetries: MAX_CHUNK_RETRIES,
  shouldRetry: shouldRetryChunk,
  getSignal: ([{ signal }]) => signal,
})

export async function fetchChunksInBatches(
  chunkIndices: number[],
  fetchOne: (chunkIndex: number) => Promise<unknown>
): Promise<void> {
  for (let i = 0; i < chunkIndices.length; i += CHUNK_FETCH_BATCH_SIZE) {
    const batch = chunkIndices.slice(i, i + CHUNK_FETCH_BATCH_SIZE)
    await Promise.all(batch.map((chunkIndex) => fetchOne(chunkIndex)))
  }
}

export const abortChunkFetches = (): void =>
  abort(ABORT_KEY, 'Chunk fetches aborted')

export const getChunkFetchSignal = (): AbortSignal | undefined =>
  getSignal(ABORT_KEY)
